import { useSnackbar } from "notistack";
import React, { createContext, ReactNode, useContext } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import APIconButton from "../components/buttons/APIconButton";
import { ApplicationScope } from "../types/App";
import { DEFAULT_ERROR_MESSAGE, ErrorCode, ErrorCodeMap } from "../types/Error";
import { ErrorCause } from "./ApiProvider";
import { useAuth } from "./AuthProvider";

export const LONG_STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours
export const SHORT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
export const MINIMUM_STALE_TIME = 10 * 1000; // 10 seconds

export type APQueryOptions = {
  appTypeScope?: ApplicationScope;
  disabled?: boolean;
  mutationOptions?: {
    successNavPath?: string;
    errorNavPaths?: Partial<Record<ErrorCode, string>>;
    successReplaceHistory?: boolean;
    errorReplaceHistory?: boolean;
    successAlertMessage?: string;
    errorAlertMessage?: string;
    refreshOnSuccess?: boolean;
  };
  keepPreviousData?: boolean;
  refetchInterval?: number;
  useCached?: boolean;
};

// Define the context with a fetch function
const APQueryContext = createContext<{
  isQueryEnabled: (options: APQueryOptions) => boolean;
  defaultOnMutateError: (options?: APQueryOptions) => (error?: Error) => void;
  defaultOnMutateSuccess: (options: APQueryOptions) => (() => void) | undefined;
  defaultOnMutateSettled: (
    invalidateList: unknown[][],
    removeList?: unknown[][]
  ) => void | undefined;
  optimisiticUpdateOnMutate: (queryKey: unknown[], data: unknown) => void;
  getDataByQueryKey: (queryKey: unknown[]) => unknown;
  alertOnLongRequest: <T>(
    request: Promise<T>,
    timeToAlert: number,
    alertMessage: string,
    timeToError?: number,
    errorMessage?: string
  ) => Promise<T>;
}>({
  isQueryEnabled: () => false,
  defaultOnMutateError: () => () => {},
  defaultOnMutateSuccess: () => undefined,
  defaultOnMutateSettled: () => undefined,
  optimisiticUpdateOnMutate: () => {},
  getDataByQueryKey: () => {},
  alertOnLongRequest: () => Promise.reject("Provider not configured"),
});

// Custom hook to use the Auth context
export const useAPQuery = () => {
  return useContext(APQueryContext);
};

// Auth Provider component
export const APQueryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { inAppScope, getScopedPageUrl } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isQueryEnabled = (options: APQueryOptions) => {
    return (
      !options.disabled &&
      (!options.appTypeScope || !!inAppScope(options.appTypeScope))
    );
  };

  const defaultOnMutateError = (options?: APQueryOptions) => {
    const errorMessage = options?.mutationOptions?.errorAlertMessage || null;

    return (error?: Error) => {
      const errorCode = error && (error.cause as ErrorCause)?.errorCode;
      const message =
        errorMessage ||
        (errorCode && ErrorCodeMap[errorCode]) ||
        DEFAULT_ERROR_MESSAGE;
      const errorNavPath =
        errorCode && options?.mutationOptions?.errorNavPaths?.[errorCode];
      const errorReplaceHistory =
        options?.mutationOptions?.errorReplaceHistory || false;

      enqueueSnackbar(message, {
        variant: "error",
        action: (key) => (
          <APIconButton
            iconName="close"
            onClick={() => closeSnackbar(key)}
            color="inherit"
          />
        ),
        autoHideDuration: null,
      });

      if (errorNavPath) {
        navigate(errorNavPath, { replace: errorReplaceHistory });
      } else if (errorCode === ErrorCode.NotFoundError) {
        navigate(getScopedPageUrl("home"), { replace: true });
      }
    };
  };

  const defaultOnMutateSuccess = (options: APQueryOptions) => {
    const successNavPath = options.mutationOptions?.successNavPath ?? null;
    const successReplaceHistory =
      options.mutationOptions?.successReplaceHistory || false;
    const refreshOnSuccess = options.mutationOptions?.refreshOnSuccess || false;
    const message = options.mutationOptions?.successAlertMessage ?? null;

    return successNavPath || message
      ? () => {
          if (message) {
            enqueueSnackbar(message, {
              variant: "success",
              action: (key) => (
                <APIconButton
                  iconName="close"
                  onClick={() => closeSnackbar(key)}
                  color="inherit"
                />
              ),
            });
          }

          if (successNavPath) {
            navigate(successNavPath, { replace: successReplaceHistory });
          } else if (refreshOnSuccess) {
            navigate(0);
          }
        }
      : undefined;
  };

  const defaultOnMutateSettled = (
    invalidateList: unknown[][],
    removeList?: unknown[][]
  ) => {
    if (removeList) {
      removeList.forEach((queryKey) => {
        queryClient.removeQueries(queryKey);
      });
    }

    return invalidateList
      ? invalidateList.forEach((queryKey) => {
          queryClient.invalidateQueries(queryKey);
        })
      : undefined;
  };

  const optimisiticUpdateOnMutate = async (
    queryKey: unknown[],
    data: unknown
  ) => {
    // // Cancel any outgoing refetches
    // // (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey });

    // Optimistically update to the new value
    queryClient.setQueryData(queryKey, data);
  };

  const getDataByQueryKey = (queryKey: unknown[]) => {
    return queryClient.getQueryData(queryKey);
  };

  const alertOnLongRequest = <T,>(
    request: Promise<T>,
    timeToAlert: number,
    alertMessage: string,
    timeToError?: number,
    errorMessage?: string
  ) => {
    const timeout = setTimeout(() => {
      enqueueSnackbar(alertMessage, {
        variant: "info",
        action: (key) => (
          <APIconButton
            iconName="close"
            onClick={() => closeSnackbar(key)}
            color="inherit"
          />
        ),
        autoHideDuration: 5000,
      });
    }, timeToAlert); // Show message if request takes more than 3 seconds

    const requestPromise = request.finally(() => {
      clearTimeout(timeout);
      closeSnackbar();
    });
    const promises = [requestPromise];
    if (timeToError && errorMessage) {
      const timeoutPromise: Promise<T> = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(errorMessage));
        }, timeToError);
      });
      promises.push(timeoutPromise);
    }

    return Promise.race(promises);
  };

  return (
    <APQueryContext.Provider
      value={{
        isQueryEnabled,
        defaultOnMutateError,
        defaultOnMutateSuccess,
        defaultOnMutateSettled,
        optimisiticUpdateOnMutate,
        getDataByQueryKey,
        alertOnLongRequest,
      }}
    >
      {children}
    </APQueryContext.Provider>
  );
};
